import type { ThemeMode } from './theme-context';

export const THEME_STORAGE_KEY = 'mdenture.theme';
const THEME_QUERY_PARAM = 'theme';

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Optional startup override from the iframe URL query string.
 * Example: ?theme=dark
 */
export function readThemeModeFromQuery(search: string): ThemeMode | null {
  const value = new URLSearchParams(search).get(THEME_QUERY_PARAM);
  return isThemeMode(value) ? value : null;
}

/** Read startup preference: query override -> storage -> light default. */
export function loadThemeMode(search: string = window.location.search): ThemeMode {
  const fromQuery = readThemeModeFromQuery(search);
  if (fromQuery !== null) {
    return fromQuery;
  }

  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeMode(raw)) return raw;
  } catch {
    /* localStorage unavailable; continue with default */
  }
  return 'light';
}

/** Persist the user's theme preference. */
export function saveThemeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    /* localStorage unavailable; preference is session-only */
  }
}

/** Resolve the rendered theme from user mode + system preference. */
export function resolveEffectiveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/** Apply the effective theme to the root element for token selection + browser UI. */
export function applyThemeToDom(effective: 'light' | 'dark'): void {
  document.documentElement.setAttribute('data-theme', effective);
  document.documentElement.style.colorScheme = effective;
}
